import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type {
	FormAnswerValue,
	FormConsentPurpose,
} from '../../forms/form-types';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { FormVersionEntity } from './form-version.entity';
import { FormEntity } from './form.entity';

/** Where a submission came from. */
export type FormSubmissionSource =
	| 'website'
	| 'storefront'
	| 'staff'
	| 'import';

/** Workflow status, separate from the spam state. */
export type FormSubmissionStatus =
	| 'new'
	| 'in_review'
	| 'converted'
	| 'linked'
	| 'dismissed';

/** How trustworthy the submission is; only `clean` runs actions. */
export type FormSubmissionSpamState =
	| 'clean'
	| 'spam'
	| 'duplicate'
	| 'unverified';

/** One consent checkbox as agreed; the text itself is in the form version. */
export interface FormSubmissionConsentModel {
	fieldKey: string;
	purpose: FormConsentPurpose | null;
	agreed: boolean;
	formVersion: number;
	policyVersions: Record<string, string> | null;
	/** Server time, ISO 8601. */
	at: string;
}

/** What a match was found on. Email and phone are confident; domain and company possible. */
export type ClientMatchSignal =
	| 'email'
	| 'contact_email'
	| 'phone'
	| 'contact_phone'
	| 'domain'
	| 'company';

/** A client or lead on file that the submission probably is. */
export interface FormSubmissionMatchModel {
	clientId: number;
	name: string | null;
	recordType: 'lead' | 'customer';
	/** The client's primary company, so a multi-company tenant can tell brands apart. */
	companyId: number | null;
	matchedOn: Array<ClientMatchSignal>;
	confidence: 'confident' | 'possible';
	/** Tenant-local user id of the client's rep. */
	repUserId: number | null;
}

/**
 * Every submission to a form is kept here, whatever its actions do (Workd Forms goal 2).
 *
 * `clientSubmissionId` is the UUID the page generates once per filled-in form and resends
 * on a retry; it is unique per form, which is what makes a retried request return the
 * stored response instead of running anything twice. It is null only for submissions
 * that came through the old `/api/portal/register` address, which cannot send one.
 *
 * `ipAddress` and `userAgent` are for abuse handling and are cleared by the retention
 * job; the answers, consents and matches live as long as the submission.
 */
@Index(
	'UK_form_submission_form_client_id',
	['fkFormId', 'clientSubmissionId'],
	{
		unique: true,
	},
)
@Index('idx_form_submission_status', ['fkPortalSiteId', 'status'], {})
@Index('idx_form_submission_spam_state', ['spamState'], {})
@Index('idx_form_submission_email', ['submitterEmail'], {})
@Index('idx_form_submission_phone', ['submitterPhone'], {})
@Index('fk_client__id', ['fkClientId'], {})
@Index('fk_user__id_assignee', ['fkUserIdAssignee'], {})
@Entity('form_submission')
export class FormSubmissionEntity extends BaseEntity {
	@Column('int', { name: 'fk_form__id', unsigned: true })
	public fkFormId!: number;

	@Column('int', { name: 'fk_form_version__id', unsigned: true })
	public fkFormVersionId!: number;

	@Column('int', { name: 'fk_portal_site__id', nullable: true, unsigned: true })
	public fkPortalSiteId!: number | null;

	@Column('char', { name: 'clientSubmissionId', nullable: true, length: 36 })
	public clientSubmissionId!: string | null;

	// The website whose address submitted it; null for the storefront.
	@Column('int', { name: 'fk_website__id', nullable: true, unsigned: true })
	public fkWebsiteId!: number | null;

	@Column('varchar', { name: 'source', length: 20 })
	public source!: FormSubmissionSource;

	// The signed-in storefront user and their client, when there was a session.
	@Column('int', { name: 'fk_public_user__id', nullable: true, unsigned: true })
	public fkPublicUserId!: number | null;

	@Column('int', {
		name: 'fk_client__id_submitter',
		nullable: true,
		unsigned: true,
	})
	public fkClientIdSubmitter!: number | null;

	@Column('varchar', { name: 'originHost', nullable: true, length: 255 })
	public originHost!: string | null;

	// Page URL and title, landing page, referrer, UTM values and ad click ids.
	@Column('json', { name: 'attribution', nullable: true })
	public attribution!: Record<string, string> | null;

	@Column('varchar', { name: 'locale', nullable: true, length: 10 })
	public locale!: string | null;

	// Sanitized answers keyed by field key.
	@Column('json', { name: 'answers' })
	public answers!: Record<string, FormAnswerValue>;

	@Column('json', { name: 'consents', nullable: true })
	public consents!: Array<FormSubmissionConsentModel> | null;

	// Copied from the identity fields, for search and matching.
	@Column('varchar', { name: 'submitterName', nullable: true, length: 255 })
	public submitterName!: string | null;

	@Column('varchar', { name: 'submitterEmail', nullable: true, length: 255 })
	public submitterEmail!: string | null;

	@Column('varchar', { name: 'submitterPhone', nullable: true, length: 64 })
	public submitterPhone!: string | null;

	@Column('varchar', { name: 'submitterCompany', nullable: true, length: 255 })
	public submitterCompany!: string | null;

	@Column('varchar', { name: 'status', length: 20, default: 'new' })
	public status!: FormSubmissionStatus;

	@Column('varchar', { name: 'spamState', length: 20, default: 'clean' })
	public spamState!: FormSubmissionSpamState;

	// How spamState was decided: Turnstile result, honeypot, time trap, blocklist rule,
	// duplicate of which submission.
	@Column('json', { name: 'spamCheck', nullable: true })
	public spamCheck!: Record<string, unknown> | null;

	@Column('int', {
		name: 'fk_form_submission__id_duplicateOf',
		nullable: true,
		unsigned: true,
	})
	public fkFormSubmissionIdDuplicateOf!: number | null;

	// Tenant-local user who owns it: the confident match's rep, else the form default.
	@Column('int', {
		name: 'fk_user__id_assignee',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdAssignee!: number | null;

	// The lead created from it, or the client it was linked or attached to. Client merge
	// repoints this and fk_client__id_submitter.
	@Column('int', { name: 'fk_client__id', nullable: true, unsigned: true })
	public fkClientId!: number | null;

	// Snapshot at submit time; the inbox filters it by the viewer's client access.
	@Column('json', { name: 'matches', nullable: true })
	public matches!: Array<FormSubmissionMatchModel> | null;

	// From the page's startedAt to submit; under two seconds is treated as a bot.
	@Column('int', { name: 'durationSeconds', nullable: true })
	public durationSeconds!: number | null;

	@Column('varchar', { name: 'ipAddress', nullable: true, length: 64 })
	public ipAddress!: string | null;

	@Column('varchar', { name: 'userAgent', nullable: true, length: 512 })
	public userAgent!: string | null;

	@ManyToOne(() => FormEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_form__id', referencedColumnName: 'id' }])
	public form!: FormEntity;

	@ManyToOne(() => FormVersionEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_form_version__id', referencedColumnName: 'id' }])
	public formVersion!: FormVersionEntity;

	@ManyToOne(() => ClientEntity, {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public client!: ClientEntity | null;
}
